import axios from "axios";
import SideNavbar from "../../../components/common/SideNavbar";
import TopNavbar from "../../../components/common/TopNavbar";
import { useEffect, useState } from "react";
import { Button, Spinner, Modal } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../../utils/TableCustomStyles";

function RolePermissionMaster() {
  const [menuDrawerShow, setMenuDrawerShow] = useState(true);
  const [data, setData] = useState([]);
  const [dataPending, setDataPending] = useState(true);
  const [currentUserRolePermissionData, setCurrentUserRolePermissionData] =
    useState([]);
  const [dataFromTopNavBar, setDataFromTopNavBar] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddLoader, setShowAddLoader] = useState(false);
  const [showUpdateLoader, setShowUpdateLoader] = useState(false);
  const [showDeleteLoader, setShowDeleteLoader] = useState(false);
  const [recordForDelete, setRecordForDelete] = useState(null);
  const user_role = sessionStorage["userrole"];

  // Canvas visibility
  const [showAddCanvas, setShowAddCanvas] = useState(false);
  const [showUpdateCanvas, setShowUpdateCanvas] = useState(false);
  const [canvasAddFullScreen, setCanvasAddFullScreen] = useState(false);
  const [canvasUpdateFullScreen, setCanvasUpdateFullScreen] = useState(false);

  // ---------- Add form state ----------
  const [addModule, setAddModule] = useState("");
  const [addSubmodule, setAddSubmodule] = useState("");
  const [addPermission, setAddPermission] = useState("view");

  // Add field errors
  const [showModuleError, setShowModuleError] = useState(false);
  const [showSubmoduleError, setShowSubmoduleError] = useState(false);
  const [showPermissionError, setShowPermissionError] = useState(false);

  // Add validation summary
  const [showValidationError, setShowValidationError] = useState(false);
  const [validationErrorArray, setValidationErrorArray] = useState([]);
  const [isValidationErrorVisible, setIsValidationErrorVisible] =
    useState(false);

  // ---------- Update form state ----------
  const [updateId, setUpdateId] = useState("");
  const [updateModule, setUpdateModule] = useState("");
  const [updateSubmodule, setUpdateSubmodule] = useState("");
  const [updatePermission, setUpdatePermission] = useState("");

  // Update field errors
  const [showUpdateModuleError, setShowUpdateModuleError] = useState(false);
  const [showUpdateSubmoduleError, setShowUpdateSubmoduleError] =
    useState(false);
  const [showUpdatePermissionError, setShowUpdatePermissionError] =
    useState(false);

  // Update validation summary
  const [showUpdateValidationError, setShowUpdateValidationError] =
    useState(false);
  const [updateValidationErrorArray, setUpdateValidationErrorArray] = useState(
    [],
  );
  const [isUpdateValidationErrorVisible, setIsUpdateValidationErrorVisible] =
    useState(false);

  // ========== Real‑time error helpers for Add ==========
  const hasAnyAddError = () => {
    return showModuleError || showSubmoduleError || showPermissionError;
  };

  const buildAddErrorMessages = () => {
    const errors = [];
    if (!addModule) errors.push("Module is required");
    if (!addSubmodule) errors.push("Submodule is required");
    if (!addPermission) errors.push("Permission is required");
    return errors;
  };

  // ========== Real‑time error helpers for Update ==========
  const hasAnyUpdateError = () => {
    return (
      showUpdateModuleError ||
      showUpdateSubmoduleError ||
      showUpdatePermissionError
    );
  };

  const buildUpdateErrorMessages = () => {
    const errors = [];
    if (!updateModule) errors.push("Module is required");
    if (!updateSubmodule) errors.push("Submodule is required");
    if (!updatePermission) errors.push("Permission is required");
    return errors;
  };

  // Tooltip toggles
  const handleCheckValidationClick = () =>
    setIsValidationErrorVisible(!isValidationErrorVisible);
  const handleUpdateCheckValidationClick = () =>
    setIsUpdateValidationErrorVisible(!isUpdateValidationErrorVisible);

  // ========== API calls ==========
  const getRolePermission = async () => {
    try {
      setDataPending(true);
      const response = await axios.get(
        process.env.REACT_APP_BACKEND_API_URL + "/user/rolepermissions/",
      );
      setData(response.data);
    } catch (err) {
      toast.error("Error in fetching role permission data! Try again.");
    } finally {
      setDataPending(false);
    }
  };

  const getCurrentUserRolePermission = async (roles) => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BACKEND_API_URL +
          "/user/role/getuserrolepermission?roles=" +
          roles,
      );
      const role_permissions_array = [];
      for (let i = 0; i < response.data.length; i++) {
        for (let j = 0; j < response?.data[i]?.permissions_array.length; j++) {
          if (
            !role_permissions_array.includes(
              response?.data[i]?.permissions_array?.[j],
            )
          ) {
            role_permissions_array.push(
              response?.data[i]?.permissions_array?.[j],
            );
          }
        }
      }
      setCurrentUserRolePermissionData(role_permissions_array);
    } catch (err) {
      toast.error("Error in fetching user role data! Try again.");
    }
  };

  useEffect(() => {
    getRolePermission();
  }, []);

  useEffect(() => {
    getCurrentUserRolePermission(user_role);
  }, [user_role]);

  // ========== Delete handlers ==========
  const handleDeleteModalClose = () => setShowDeleteModal(false);
  const handleDeleteModalOpen = (row) => {
    setShowDeleteModal(true);
    setRecordForDelete(row.id);
  };

  const deleteSelectedRecord = async (id) => {
    setShowDeleteLoader(true);
    try {
      await axios.delete(
        process.env.REACT_APP_BACKEND_API_URL +
          "/user/rolepermissions/" +
          id +
          "/",
      );
      setShowDeleteModal(false);
      getRolePermission();
      toast.success("Record deleted successfully");
    } catch (err) {
      toast.error("Error in deleting record! Try again.");
    }
    setShowDeleteLoader(false);
  };

  // ========== Add logic ==========
  const resetAddForm = () => {
    setAddModule("");
    setAddSubmodule("");
    setAddPermission("view");
    setShowModuleError(false);
    setShowSubmoduleError(false);
    setShowPermissionError(false);
    setShowValidationError(false);
    setValidationErrorArray([]);
    setIsValidationErrorVisible(false);
  };

  const handleAddModalClose = () => {
    resetAddForm();
    setShowAddCanvas(false);
    setCanvasAddFullScreen(false);
  };

  const addNewRecord = async (e) => {
    e.preventDefault();

    setShowModuleError(!addModule);
    setShowSubmoduleError(!addSubmodule);
    setShowPermissionError(!addPermission);
    setValidationErrorArray(buildAddErrorMessages());

    if (!addModule || !addSubmodule || !addPermission) {
      setShowValidationError(true);
      return;
    }

    setShowAddLoader(true);

    const payload = {
      module: addModule.toLowerCase(),
      submodule: addSubmodule.toLowerCase(),
      permission: addPermission,
    };

    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_API_URL + "/user/rolepermissions/",
        payload,
        { headers: { "content-type": "application/json" } },
      );
      if (response.status === 201) {
        toast.success("Role Permission Created Successfully");
        handleAddModalClose();
        getRolePermission();
      }
    } catch (err) {
      toast.error("Error creating role permission");
    } finally {
      setShowAddLoader(false);
    }
  };

  // ========== Update logic ==========
  const resetUpdateForm = () => {
    setUpdateId("");
    setUpdateModule("");
    setUpdateSubmodule("");
    setUpdatePermission("");
    setShowUpdateModuleError(false);
    setShowUpdateSubmoduleError(false);
    setShowUpdatePermissionError(false);
    setShowUpdateValidationError(false);
    setUpdateValidationErrorArray([]);
    setIsUpdateValidationErrorVisible(false);
  };

  const handleUpdateModalClose = () => {
    resetUpdateForm();
    setShowUpdateCanvas(false);
    setCanvasUpdateFullScreen(false);
  };

  const handleSetUpdateData = (row) => {
    setUpdateId(row.id);
    setUpdateModule(row.module);
    setUpdateSubmodule(row.submodule);
    setUpdatePermission(row.permission);
    // Reset errors
    setShowUpdateModuleError(false);
    setShowUpdateSubmoduleError(false);
    setShowUpdatePermissionError(false);
    setShowUpdateValidationError(false);
    setUpdateValidationErrorArray([]);
  };

  const updateRecord = async (e) => {
    e.preventDefault();

    setShowUpdateModuleError(!updateModule);
    setShowUpdateSubmoduleError(!updateSubmodule);
    setShowUpdatePermissionError(!updatePermission);
    setUpdateValidationErrorArray(buildUpdateErrorMessages());

    if (!updateModule || !updateSubmodule || !updatePermission) {
      setShowUpdateValidationError(true);
      return;
    }

    setShowUpdateLoader(true);

    const payload = {
      module: updateModule.toLowerCase(),
      submodule: updateSubmodule.toLowerCase(),
      permission: updatePermission,
    };

    try {
      const response = await axios.put(
        process.env.REACT_APP_BACKEND_API_URL +
          "/user/rolepermissions/" +
          updateId +
          "/",
        payload,
        { headers: { "content-type": "application/json" } },
      );
      if (response.status === 200) {
        toast.success("Role Permission Updated Successfully");
        handleUpdateModalClose();
        getRolePermission();
      }
    } catch (err) {
      toast.error("Error updating role permission");
    } finally {
      setShowUpdateLoader(false);
    }
  };

  // ========== Table columns ==========
  const columns = [
    { name: "Module", selector: (row) => row.module, sortable: true },
    { name: "Sub Module", selector: (row) => row.submodule, sortable: true },
    { name: "Permission", selector: (row) => row.permission, sortable: true },
    {
      name: "Action",
      center: true,
      selector: (row) => (
        <div>
          {currentUserRolePermissionData?.includes(
            "master:rolepermission:update",
          ) && (
            <Button
              className="btn btn-sm btn-inox-green border-0"
              title="Update"
              onClick={() => {
                handleSetUpdateData(row);
                setShowUpdateCanvas(true);
              }}
            >
              <i className="fa fa-edit"></i>
            </Button>
          )}
          {currentUserRolePermissionData?.includes(
            "master:rolepermission:delete",
          ) && (
            <Button
              className="btn btn-sm bg-danger border-0 mx-2"
              title="Delete"
              onClick={() => handleDeleteModalOpen(row)}
            >
              <i className="fa fa-trash"></i>
            </Button>
          )}
        </div>
      ),
    },
  ];

  const handleDataFromTopNavBar = (permissiondata) =>
    setDataFromTopNavBar(permissiondata);

  return (
    <div className="page-content-bg">
      <ToastContainer />
      <TopNavbar
        sidebarCollapsed={menuDrawerShow}
        topNavbarStatic={true}
        sendDataToParent={handleDataFromTopNavBar}
      />
      <SideNavbar
        sidebarCollapsed={menuDrawerShow}
        activeMenu="Master"
        activeSubMenu="AdminMaster"
        activeSubMenuLevel1="RolePermissionMaster"
        permissionFromTopNavbar={dataFromTopNavBar}
      />

      <div
        className={
          menuDrawerShow
            ? "row main-content-collapsed text-left py-0"
            : "row main-content text-left py-0"
        }
      >
        <div className="col-12 page-header">
          <div className="row">
            <div className="col-6 py-1">
              <Button
                className="btn btn-sm btn-inox-green border-0 mx-2"
                onClick={() => setMenuDrawerShow(!menuDrawerShow)}
              >
                {menuDrawerShow ? (
                  <span>
                    <i className="fa fa-chevron-left mx-1"></i>
                  </span>
                ) : (
                  <span>
                    <i className="fa fa-chevron-right mx-1"></i>
                  </span>
                )}
              </Button>
              <span className="page-header-text">Role Permission Master</span>
            </div>
            <div className="col-6 py-1" style={{ paddingRight: 30 }}>
              {currentUserRolePermissionData?.includes(
                "master:rolepermission:add",
              ) ? (
                <Button
                  className="btn btn-sm btn-inox-green border-0 px-3 float-right"
                  onClick={() => {
                    resetAddForm();
                    setShowAddCanvas(true);
                  }}
                >
                  <i
                    className="fa fa-plus fa-lg text-right"
                    style={{ paddingRight: 10 }}
                  ></i>
                  <span className="fw-bold">Add Role Permission</span>
                </Button>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>

        <div
          className="col-12 text-left"
          style={{
            marginTop: -5,
            paddingLeft: 20,
            paddingRight: 30,
            zIndex: 100,
          }}
        >
          <DataTable
            columns={columns}
            data={data}
            progressPending={dataPending}
            progressComponent={
              <Spinner
                className="mx-5 text-inox-green table-spinner"
                as="span"
                animation="border"
                size="lg"
                role="status"
                aria-hidden="true"
              />
            }
            noDataComponent={
              <p className="text-center table-no-data">No Data Found</p>
            }
            pagination
            highlightOnHover
            fixedHeader
            customStyles={tableCustomStyles}
            dense
          />
        </div>
      </div>

      {/* Delete Modal */}
      <Modal
        size="sm"
        show={showDeleteModal}
        onHide={handleDeleteModalClose}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Do you want to delete this record?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteModalClose}>
            Close
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteSelectedRecord(recordForDelete)}
          >
            {showDeleteLoader ? (
              <Spinner
                className="mx-3 text-white"
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ------------------------ Update Canvas ------------------- */}
      <div
        className={
          canvasUpdateFullScreen && showUpdateCanvas
            ? "offcanvas offcanvas-bottom h-100 show"
            : !canvasUpdateFullScreen && showUpdateCanvas
              ? "offcanvas offcanvas-bottom h-50 show"
              : "offcanvas offcanvas-bottom h-50"
        }
        tabIndex="-1"
        aria-labelledby="updateCanvasLabel"
      >
        <div className="offcanvas-header w-100">
          <div className="row w-100">
            <div className="col-6">
              <h4 id="updateCanvasLabel" className="float-left">
                Update Role Permission
              </h4>
            </div>
            <div className="col-6">
              <button
                type="button"
                className="btn btn-danger btn-sm float-right"
                aria-label="Close"
                title="Close"
                onClick={handleUpdateModalClose}
              >
                <i className="fa fa-times text-white"></i>
              </button>
              {canvasUpdateFullScreen ? (
                <button
                  type="button"
                  className="btn btn-inox-green btn-sm mx-2 float-right"
                  title="Half Screen"
                  onClick={() => setCanvasUpdateFullScreen(false)}
                >
                  <i className="fa fa-window-minimize text-white"></i>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-inox-green btn-sm mx-2 float-right"
                  title="Full Screen"
                  onClick={() => setCanvasUpdateFullScreen(true)}
                >
                  <i className="fa fa-window-maximize text-white"></i>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={updateRecord}>
            <div className="row justify-content-center">
              <div className="col-4">
                <div className="p-2 mt-2 quote-input-column">
                  <div className="mb-3 text-left">
                    <label className="required-label" htmlFor="update_module">
                      Module:
                      <span className="text-danger">
                        {showUpdateModuleError ? " Required" : ""}
                      </span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="update_module"
                      placeholder="Enter Module Name"
                      value={updateModule}
                      onChange={(e) => {
                        setUpdateModule(e.target.value);
                        setShowUpdateModuleError(false);
                        setShowUpdateValidationError(false);
                        setUpdateValidationErrorArray(
                          buildUpdateErrorMessages(),
                        );
                      }}
                      onBlur={(e) => {
                        setShowUpdateModuleError(e.target.value === "");
                        setUpdateValidationErrorArray(
                          buildUpdateErrorMessages(),
                        );
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="col-4">
                <div className="p-2 mt-2 quote-input-column">
                  <div className="mb-3 text-left">
                    <label
                      className="required-label"
                      htmlFor="update_submodule"
                    >
                      Submodule:
                      <span className="text-danger">
                        {showUpdateSubmoduleError ? " Required" : ""}
                      </span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="update_submodule"
                      placeholder="Enter Submodule Name"
                      value={updateSubmodule}
                      onChange={(e) => {
                        setUpdateSubmodule(e.target.value);
                        setShowUpdateSubmoduleError(false);
                        setShowUpdateValidationError(false);
                        setUpdateValidationErrorArray(
                          buildUpdateErrorMessages(),
                        );
                      }}
                      onBlur={(e) => {
                        setShowUpdateSubmoduleError(e.target.value === "");
                        setUpdateValidationErrorArray(
                          buildUpdateErrorMessages(),
                        );
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="col-4">
                <div className="p-2 mt-2 quote-input-column">
                  <div className="mb-3 text-left">
                    <label
                      className="required-label"
                      htmlFor="update_permission"
                    >
                      Permission:
                      <span className="text-danger">
                        {showUpdatePermissionError ? " Required" : ""}
                      </span>
                    </label>
                    <select
                      className="form-select"
                      id="update_permission"
                      value={updatePermission}
                      onChange={(e) => {
                        setUpdatePermission(e.target.value);
                        setShowUpdatePermissionError(false);
                        setShowUpdateValidationError(false);
                        setUpdateValidationErrorArray(
                          buildUpdateErrorMessages(),
                        );
                      }}
                      onBlur={(e) => {
                        setShowUpdatePermissionError(e.target.value === "");
                        setUpdateValidationErrorArray(
                          buildUpdateErrorMessages(),
                        );
                      }}
                    >
                      <option value="view">View</option>
                      <option value="add">Add</option>
                      <option value="update">Update</option>
                      <option value="delete">Delete</option>
                      <option value="upload">Upload</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button + Error Tooltip */}
            <div className="text-center mt-4">
              <div className="canvas-btn-row">
                <Button
                  type="submit"
                  className="btn btn-inox-green border-0 canvas-button"
                  disabled={hasAnyUpdateError() || showUpdateLoader}
                >
                  {showUpdateLoader ? (
                    <Spinner
                      className="mx-4"
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <>
                      <i className="fa fa-edit"></i>{" "}
                      <span className="mx-2">Update</span>
                    </>
                  )}
                </Button>

                {(hasAnyUpdateError() || showUpdateValidationError) && (
                  <div className="error-tooltip-wrapper">
                    <Button
                      style={{ backgroundColor: "#C21E22", border: "#f82e2e" }}
                      title="Show Errors"
                      className="mx-2"
                      onClick={handleUpdateCheckValidationClick}
                      onMouseLeave={() =>
                        setIsUpdateValidationErrorVisible(false)
                      }
                    >
                      <i className="fa fa-exclamation"></i>
                    </Button>
                    {isUpdateValidationErrorVisible && (
                      <div className="validation-error-tooltip">
                        <ul className="mb-0 px-4">
                          {updateValidationErrorArray.map((error, index) => (
                            <li key={index} className="text-left small pb-0">
                              {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* ------------------------ Add Canvas ------------------- */}
      <div
        className={
          canvasAddFullScreen && showAddCanvas
            ? "offcanvas offcanvas-bottom h-100 show"
            : !canvasAddFullScreen && showAddCanvas
              ? "offcanvas offcanvas-bottom h-50 show"
              : "offcanvas offcanvas-bottom h-50"
        }
        tabIndex="-1"
        aria-labelledby="addCanvasLabel"
      >
        <div className="offcanvas-header w-100">
          <div className="row w-100">
            <div className="col-6">
              <h4 id="addCanvasLabel" className="float-left">
                Add New Role Permission
              </h4>
            </div>
            <div className="col-6">
              <button
                type="button"
                className="btn btn-danger btn-sm float-right"
                aria-label="Close"
                title="Close"
                onClick={handleAddModalClose}
              >
                <i className="fa fa-times text-white"></i>
              </button>
              {canvasAddFullScreen ? (
                <button
                  type="button"
                  className="btn btn-inox-green btn-sm mx-2 float-right"
                  title="Half Screen"
                  onClick={() => setCanvasAddFullScreen(false)}
                >
                  <i className="fa fa-window-minimize text-white"></i>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-inox-green btn-sm mx-2 float-right"
                  title="Full Screen"
                  onClick={() => setCanvasAddFullScreen(true)}
                >
                  <i className="fa fa-window-maximize text-white"></i>
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={addNewRecord}>
            <div className="row justify-content-center">
              <div className="col-4">
                <div className="p-2 mt-2 quote-input-column">
                  <div className="mb-3 text-left">
                    <label className="required-label" htmlFor="add_module">
                      Module:
                      <span className="text-danger">
                        {showModuleError ? " Required" : ""}
                      </span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="add_module"
                      placeholder="Enter Module Name"
                      value={addModule}
                      onChange={(e) => {
                        setAddModule(e.target.value);
                        setShowModuleError(false);
                        setShowValidationError(false);
                        setValidationErrorArray(buildAddErrorMessages());
                      }}
                      onBlur={(e) => {
                        setShowModuleError(e.target.value === "");
                        setValidationErrorArray(buildAddErrorMessages());
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="col-4">
                <div className="p-2 mt-2 quote-input-column">
                  <div className="mb-3 text-left">
                    <label className="required-label" htmlFor="add_submodule">
                      Submodule:
                      <span className="text-danger">
                        {showSubmoduleError ? " Required" : ""}
                      </span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="add_submodule"
                      placeholder="Enter Submodule Name"
                      value={addSubmodule}
                      onChange={(e) => {
                        setAddSubmodule(e.target.value);
                        setShowSubmoduleError(false);
                        setShowValidationError(false);
                        setValidationErrorArray(buildAddErrorMessages());
                      }}
                      onBlur={(e) => {
                        setShowSubmoduleError(e.target.value === "");
                        setValidationErrorArray(buildAddErrorMessages());
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="col-4">
                <div className="p-2 mt-2 quote-input-column">
                  <div className="mb-3 text-left">
                    <label className="required-label" htmlFor="add_permission">
                      Permission:
                      <span className="text-danger">
                        {showPermissionError ? " Required" : ""}
                      </span>
                    </label>
                    <select
                      className="form-select"
                      id="add_permission"
                      value={addPermission}
                      onChange={(e) => {
                        setAddPermission(e.target.value);
                        setShowPermissionError(false);
                        setShowValidationError(false);
                        setValidationErrorArray(buildAddErrorMessages());
                      }}
                      onBlur={(e) => {
                        setShowPermissionError(e.target.value === "");
                        setValidationErrorArray(buildAddErrorMessages());
                      }}
                    >
                      <option value="view">View</option>
                      <option value="add">Add</option>
                      <option value="update">Update</option>
                      <option value="delete">Delete</option>
                      <option value="upload">Upload</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button + Error Tooltip */}
            <div className="text-center mt-4">
              <div className="canvas-btn-row">
                <Button
                  type="submit"
                  className="btn btn-inox-green border-0 canvas-button"
                  disabled={hasAnyAddError() || showAddLoader}
                >
                  {showAddLoader ? (
                    <Spinner
                      className="mx-4"
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <>
                      <i className="fa fa-plus"></i>{" "}
                      <span className="mx-2">Add</span>
                    </>
                  )}
                </Button>

                {(hasAnyAddError() || showValidationError) && (
                  <div className="error-tooltip-wrapper">
                    <Button
                      style={{ backgroundColor: "#C21E22", border: "#f82e2e" }}
                      title="Show Errors"
                      className="mx-2"
                      onClick={handleCheckValidationClick}
                      onMouseLeave={() => setIsValidationErrorVisible(false)}
                    >
                      <i className="fa fa-exclamation"></i>
                    </Button>
                    {isValidationErrorVisible && (
                      <div className="validation-error-tooltip">
                        <ul className="mb-0 px-4">
                          {validationErrorArray.map((error, index) => (
                            <li key={index} className="text-left small pb-0">
                              {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RolePermissionMaster;
